import React, { useEffect, useState } from "react";
import "../../../CSS/Candidate.css";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  GridItem,
  useToast
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  CloseIcon,
  ExternalLinkIcon,
  HamburgerIcon,
  PhoneIcon,
} from "@chakra-ui/icons";
import {
  fetchCandidateListAsync,
  selectCandidates,
  updateJobStatusAsync,
} from "../CandidateSlice/Basic/CandidateSlice";
import { TimeDifference } from "../../../../common/TimeDifference";
import { Link } from "react-router-dom";
import Modal from "../../Modal";

const InterviewScheduled = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const dispatch = useDispatch();
  const candidates = useSelector(selectCandidates);
  const records = candidates?.records?.data;
  const toast = useToast();

  const total = candidates?.status_counts?.interview_scheduled;
  const CandidatesPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(total / CandidatesPerPage);

  // Utility Functions
  const getRoleBadgeColor = (skills) => {
    if (!skills || skills.length === 0) return { bg: "#e5e7eb", color: "#374151", text: "General" }

    const skillTitles = skills.map((s) => s?.skill?.title?.toLowerCase()).join(" ")

    if (skillTitles.includes("react") || skillTitles.includes("frontend") || skillTitles.includes("javascript")) {
      return { bg: "#dbeafe", color: "#1e40af", text: "Frontend" }
    }
    if (skillTitles.includes("node") || skillTitles.includes("backend") || skillTitles.includes("python")) {
      return { bg: "#dcfce7", color: "#166534", text: "Backend" }
    }
    if (skillTitles.includes("design") || skillTitles.includes("ui") || skillTitles.includes("ux")) {
      return { bg: "#fef3c7", color: "#92400e", text: "UI Design" }
    }
    if (skillTitles.includes("marketing") || skillTitles.includes("digital")) {
      return { bg: "#fce7f3", color: "#be185d", text: "Marketing" }
    }
    if (skillTitles.includes("data") || skillTitles.includes("analytics")) {
      return { bg: "#e0e7ff", color: "#3730a3", text: "Data" }
    }

    return { bg: "#f3f4f6", color: "#374151", text: "General" }
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "shortlist":
        return "status-green"
      case "rejected":
        return "status-red"
      case "on_hold":
        return "status-yellow"
      default:
        return "status-blue"
    }
  }

  // Data Fetching
  const fetchCandidatesData = async () => {
    setIsLoading(true);
    try {
      await dispatch(fetchCandidateListAsync({
        page: currentPage,
        status: "interview_scheduled"
      })).unwrap();
    } catch (error) {
      console.error("Error fetching interview scheduled candidates:", error);
      toast({
        title: "Error",
        description: "Failed to fetch candidates data",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Event Handlers
  const handleApplicantClick = (candidate) => {
    if (candidate.is_candidate_unlock) {
      const url = `/employer-dashboard/show-candidate/${candidate.candidate.uuid}`;
      window.open(url, "_blank");

      toast({
        title: "Opening candidate profile",
        description: `Viewing ${candidate.candidate?.name}'s detailed profile`,
        status: "info",
        duration: 2000,
        isClosable: true,
        position: "top-right"
      });
    } else {
      toast({
        title: "Unlock required",
        description: "You need to unlock this candidate before viewing their profile.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleHire = async (uuid) => {
    if (isActionLoading) return;

    setIsActionLoading(true);
    try {
      await dispatch(updateJobStatusAsync({ uuid, newStatus: "hired" })).unwrap();

      toast({
        title: "Success",
        description: "Candidate hired successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right"
      });

      await fetchCandidatesData();
    } catch (error) {
      console.error("Error hiring candidate:", error);
      toast({
        title: "Error",
        description: "Failed to hire candidate",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleReject = async (uuid) => {
    if (isActionLoading) return;

    setIsActionLoading(true);
    try {
      await dispatch(updateJobStatusAsync({ uuid, newStatus: "rejected" })).unwrap();

      toast({
        title: "Success",
        description: "Candidate rejected successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right"
      });

      await fetchCandidatesData();
    } catch (error) {
      console.error("Error rejecting candidate:", error);
      toast({
        title: "Error",
        description: "Failed to reject candidate",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleOnHold = async (uuid) => {
    if (isActionLoading) return;

    setIsActionLoading(true);
    try {
      await dispatch(updateJobStatusAsync({ uuid, newStatus: "on_hold" })).unwrap();

      toast({
        title: "Success",
        description: "Candidate put on hold successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right"
      });

      await fetchCandidatesData();
    } catch (error) {
      console.error("Error putting candidate on hold:", error);
      toast({
        title: "Error",
        description: "Failed to put candidate on hold",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleOpenModal = (candidate) => {
    setSelectedCandidate(candidate);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedCandidate(null);
  };

  // Component Definitions
  const DetailedCandidateCard = ({ candidate }) => {
    const roleBadge = getRoleBadgeColor(candidate.candidate?.it_skills);

    return (
      <div className="detailed-card">
        {/* Header Section */}
        <div className="card-header-section">
          <div className="avatar-info">
            <div className="basic-info">
              <h3 className="candidate-name-detailed hover:underline mb-2" onClick={() => handleApplicantClick(candidate)}>
                {candidate.candidate?.name}
              </h3>

              <div className="status-badges">
                <span className={`unlock-badge ${candidate.is_candidate_unlock ? "unlocked" : "locked"}`}>
                  {candidate.is_candidate_unlock ? "Unlocked" : "Locked"}
                </span>
                <span className="match-badge">{candidate?.profile_matching_percantage}% Match</span>
              </div>
            </div>
          </div>
          <div className="quick-stats">
            <div className={`stat-item ${getStatusBadgeClass(candidate?.status)}`}>
              <span className="font-medium">
                {candidate?.status === "interview_scheduled" ? "Interview Scheduled" : candidate?.status?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="card-details">
          <div className="detail-row">
            <div className="detail-item">
              <i className="fa-solid fa-user detail-icon"></i>
              <span className="detail-text">{candidate.candidate?.basic_details?.gender || "Not provided"}</span>
            </div>
            <div className="detail-item">
              <i className="fa-solid fa-briefcase detail-icon"></i>
              <span className="detail-text">
                {candidate.candidate?.basic_details?.experience_year === 0 &&
                  candidate.candidate?.basic_details?.experience_month === 0
                  ? "Fresher"
                  : `${candidate.candidate?.basic_details?.experience_year}y ${candidate.candidate?.basic_details?.experience_month}m`}
              </span>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-item">
              <i className="fa-solid fa-location-dot detail-icon"></i>
              <span className="detail-text">
                {candidate.candidate?.basic_details?.city?.name}, {candidate.candidate?.basic_details?.state?.name}
              </span>
            </div>
            <div className="detail-item">
              <i className="fa-solid fa-calendar detail-icon"></i>
              <span className="detail-text">{candidate.candidate?.basic_details?.dob || "Not provided"}</span>
            </div>
          </div>

          <div className="skills-section">
            <div className="skills-label">
              <i className="fa-solid fa-code detail-icon"></i>
              <span>Skills:</span>
            </div>
            <div className="skills-list">
              {candidate.candidate?.it_skills?.length > 0
                ? candidate.candidate.it_skills
                  .slice(0, 3)
                  .map((skillObj) => skillObj?.skill?.title)
                  .join(", ") +
                (candidate.candidate.it_skills.length > 3 ? ` +${candidate.candidate.it_skills.length - 3} more` : "")
                : "No skills listed"}
            </div>
          </div>

          <div className="job-section">
            <div className="job-info">
              <i className="fa-solid fa-building detail-icon"></i>
              <Link to={`/employer-dashboard/manage-jobs/${candidate?.job?.uuid}`} className="job-link hover:underline">
                {candidate?.job?.job_title?.title || "No Job Title"}
              </Link>
            </div>
          </div>

          {candidate?.candidate?.current_company_details?.current_company && (
            <div className="job-section">
              <div className="job-info">
                <i className="fa-solid fa-building detail-icon"></i>
                <div className="job-link">
                  Current: {candidate?.candidate?.current_company_details?.current_company || "No Current Company"}
                </div>
              </div>
            </div>
          )}

          {candidate?.candidate?.current_company_details?.notice_period && (
            <div className="job-section">
              <div className="job-info">
                <i className="fa-solid fa-clock detail-icon"></i>
                <div className="job-link">
                  Notice: {candidate?.candidate?.current_company_details?.notice_period || "No Notice Period"}
                </div>
              </div>
            </div>
          )}

          <div className="time-info">
            <i className="fa-solid fa-clock detail-icon"></i>
            <TimeDifference dateTimeString={candidate.created_at} />
          </div>
        </div>

        {/* Actions Section */}
        <div className="card-actions">
          <Tooltip label="Hire Candidate" placement="top" bg="gray.100" color="black">
            <button
              className={`action-btn-detailed btn-green ${isActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => !isActionLoading && handleHire(candidate.uuid)}
              title="Hire"
              disabled={isActionLoading}
            >
              <i className="fa-solid fa-check"></i>
            </button>
          </Tooltip>

          <Tooltip label="Reject Candidate" placement="top" bg="gray.100" color="black">
            <button
              className={`action-btn-detailed btn-red ${isActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => !isActionLoading && handleReject(candidate.uuid)}
              title="Reject"
              disabled={isActionLoading}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </Tooltip>

          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<HamburgerIcon />}
              variant="outline"
              colorScheme="yellow"
              isDisabled={isActionLoading}
              size="sm"
            />
            <MenuList>
              <MenuItem
                icon={<PhoneIcon />}
                onClick={() => {
                  let phone = candidate.candidate?.phone;

                  if (!phone) {
                    alert("Phone number not available");
                    return;
                  }

                  phone = String(phone);
                  const sanitizedPhone = phone.replace(/[^\d+]/g, "");
                  const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent);

                  if (isMobile) {
                    window.location.href = `tel:${sanitizedPhone}`;
                  } else {
                    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(sanitizedPhone)}`;
                    window.open(searchUrl, "_blank");
                  }
                }}
              >
                Call
              </MenuItem>
              <MenuItem
                icon={<ExternalLinkIcon />}
                onClick={() => handleOpenModal(candidate)}
              >
                Setup Reschedule Interview
              </MenuItem>
              <MenuItem
                icon={<CloseIcon />}
                onClick={() => !isActionLoading && handleOnHold(candidate.uuid)}
                isDisabled={isActionLoading}
              >
                Put On Hold
              </MenuItem>
            </MenuList>
          </Menu>
        </div>
      </div>
    );
  };

  const LoadingCard = () => (
    <div className="detailed-card loading">
      <div className="card-header-section">
        <div className="avatar-info">
          <div className="basic-info">
            <div className="skeleton skeleton-name"></div>
            <div className="skeleton skeleton-email"></div>
            <div className="skeleton skeleton-badges"></div>
          </div>
        </div>
      </div>
      <div className="card-details">
        <div className="skeleton skeleton-line"></div>
        <div className="skeleton skeleton-line"></div>
        <div className="skeleton skeleton-line"></div>
      </div>
    </div>
  );

  // Effects
  useEffect(() => {
    fetchCandidatesData();
  }, [dispatch, currentPage]);

  return (
    <div>
      <div className="py-4">
        {isLoading ? (
          <div className="detailed-grid">
            {[...Array(9)].map((_, index) => (
              <LoadingCard key={index} />
            ))}
          </div>
        ) : records && records.filter(candidate => candidate.status === "interview_scheduled").length > 0 ? (
          <div className="detailed-grid">
            {records
              .filter(candidate => candidate.status === "interview_scheduled")
              .map((candidate) => (
                <DetailedCandidateCard key={candidate.id} candidate={candidate} />
              ))}
          </div>
        ) : (
          <div className="no-candidates">
            <div className="no-candidates-text">No interview scheduled candidates found</div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination">
              <button
                className={`pagination-btn ${currentPage === 1 || isActionLoading ? "disabled" : ""}`}
                onClick={handlePrevPage}
                disabled={currentPage === 1 || isActionLoading}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={`pagination-btn pagination-btn-primary ${currentPage === totalPages || isActionLoading ? "disabled" : ""}`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages || isActionLoading}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalVisible && selectedCandidate && (
        <Modal candidate={selectedCandidate} onClose={handleCloseModal} />
      )}

      <style jsx>{`
        .detailed-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 16px;
        }

        .detailed-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
          cursor: pointer;
          overflow: hidden;
        }

        .detailed-card:hover {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          transform: translateY(-2px);
        }

        .card-header-section {
          padding: 20px 20px 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 1px solid #f3f4f6;
        }

        .avatar-info {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          flex: 1;
        }

        .basic-info {
          flex: 1;
          min-width: 0;
        }

        .candidate-name-detailed {
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 4px 0;
          text-transform: capitalize;
          cursor: pointer;
        }

        .status-badges {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .unlock-badge {
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 500;
        }

        .unlock-badge.unlocked {
          background-color: #dcfce7;
          color: #166534;
        }

        .unlock-badge.locked {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .match-badge {
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 500;
          background-color: #dbeafe;
          color: #1e40af;
        }

        .quick-stats {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px;
        }

        .stat-item {
          text-align: center;
          padding: 6px 10px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .card-details {
          padding: 16px 20px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          gap: 16px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
        }

        .detail-icon {
          color: #6b7280;
          font-size: 12px;
          width: 14px;
          flex-shrink: 0;
        }

        .detail-text {
          font-size: 13px;
          color: #4b5563;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .skills-section {
          margin-bottom: 12px;
        }

        .skills-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 4px;
        }

        .skills-list {
          font-size: 12px;
          color: #6b7280;
          line-height: 1.4;
          padding-left: 22px;
        }

        .job-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          gap: 12px;
        }

        .job-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
        }

        .job-link {
          font-size: 13px;
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .job-link:hover {
          color: #2563eb;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .status-green {
          background-color: #dcfce7;
          color: #166534;
        }

        .status-red {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .status-yellow {
          background-color: #fef3c7;
          color: #92400e;
        }

        .status-blue {
          background-color: #dbeafe;
          color: #1e40af;
        }

        .time-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          color: #9ca3af;
        }

        .card-actions {
          display: flex;
          justify-content: space-evenly;
          padding: 12px 20px 16px 20px;
          border-top: 1px solid #f3f4f6;
          gap: 8px;
        }

        .action-btn-detailed {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .btn-green {
          border-color: #10b981;
          color: #10b981;
        }

        .btn-green:hover {
          background-color: #ecfdf5;
        }

        .btn-red {
          border-color: #ef4444;
          color: #ef4444;
        }

        .btn-red:hover {
          background-color: #fef2f2;
        }

        .btn-yellow {
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .btn-yellow:hover {
          background-color: #fffbeb;
        }

        .btn-gray {
          border-color: #9ca3af;
          color: #9ca3af;
        }

        .btn-gray:hover {
          background-color: #f9fafb;
        }

        /* Loading States */
        .loading {
          pointer-events: none;
        }

        .skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 4px;
        }

        .skeleton-name {
          height: 18px;
          width: 140px;
          margin-bottom: 4px;
        }

        .skeleton-email {
          height: 13px;
          width: 180px;
          margin-bottom: 8px;
        }

        .skeleton-badges {
          height: 16px;
          width: 120px;
        }

        .skeleton-line {
          height: 13px;
          margin-bottom: 8px;
        }

        .skeleton-line:nth-child(1) { width: 100%; }
        .skeleton-line:nth-child(2) { width: 85%; }
        .skeleton-line:nth-child(3) { width: 70%; }

        @keyframes loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .no-candidates {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          padding: 32px;
          text-align: center;
          border: 1px solid #e5e7eb;
        }

        .no-candidates-text {
          color: #6b7280;
          font-size: 18px;
        }

        .pagination-container {
          display: flex;
          justify-content: center;
          margin-top: 32px;
        }

        .pagination {
          display: flex;
          align-items: center;
          gap: 16px;
          background-color: #f3f4f6;
          padding: 16px;
          border-radius: 8px;
        }

        .pagination-btn {
          background-color: #6b7280;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-size: 14px;
        }

        .pagination-btn-primary {
          background-color: #3b82f6;
        }

        .pagination-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          font-weight: 600;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .detailed-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .card-header-section {
            flex-direction: column;
            gap: 12px;
          }
          
          .detail-row {
            flex-direction: column;
            gap: 8px;
          }
          
          .job-section {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default InterviewScheduled;